import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from "firebase/firestore";

import { logger } from "@/lib/logger";

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Boot-time diagnostic. Visible in the production console only when
// `localStorage.setItem("jmd:authDebug", "1")` is set (or always in dev).
// Lets you confirm the build picked up the correct env vars.
if (
  import.meta.env.DEV ||
  (typeof window !== "undefined" &&
    (() => {
      try {
        return window.localStorage.getItem("jmd:authDebug") === "1";
      } catch {
        return false;
      }
    })())
) {
  const apiKeyPreview = config.apiKey
    ? `${config.apiKey.slice(0, 8)}… (${config.apiKey.length} chars)`
    : "(MISSING)";
  logger.info("[firebase] initialized", {
    projectId: config.projectId || "(MISSING)",
    authDomain: config.authDomain || "(MISSING)",
    apiKeyPreview,
    hasAppId: Boolean(config.appId),
  });
}

export const firebaseApp: FirebaseApp = initializeApp(config);

/**
 * Firestore with persistent IndexedDB cache. Survives reloads, supports
 * multiple tabs. In environments without IndexedDB (private browsing on iOS)
 * the SDK silently falls back to in-memory cache.
 */
export const db: Firestore = (() => {
  try {
    return initializeFirestore(firebaseApp, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  } catch (err) {
    logger.warn("Firestore persistent cache unavailable; using in-memory", {
      err: String(err),
    });
    return initializeFirestore(firebaseApp, {});
  }
})();

export function firebaseReady(): boolean {
  return Boolean(config.apiKey && config.projectId);
}

// Auth lives in firebase-auth.ts (lazy-loaded with the admin bundle).
// Storage lives in firebase-storage.ts (lazy-loaded when uploads are wired).
